import React, { useContext } from "react";
import styled from "styled-components";

import LocaleContext from "@/i18n";
import { IThemeProps } from "@/styles";

type FormRadioProps = {
  name: string;
  label: string;
  onChange: (value: string) => void;
  options: string[];
  currentValue?: string;
};

const FormRadio: React.FC<FormRadioProps> = (
  props: FormRadioProps
): JSX.Element => {
  const { name, onChange, options, currentValue, label } = props;

  const localeContext = useContext(LocaleContext);
  const { translation } = localeContext;

  return (
    <fieldset>
      <RadioLegend>{label}</RadioLegend>
      {options.map((option: string) => (
        <RadioLabel htmlFor={option} key={`option-${option}`}>
          <Radio
            data-qa={`input.radio.${option}`}
            id={option}
            name={name}
            value={option}
            defaultChecked={currentValue === name}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              onChange(event.target.value)
            }
          />
          <RadioLabelText>
            {(translation.registration as any)[option] ?? option}
          </RadioLabelText>
        </RadioLabel>
      ))}
    </fieldset>
  );
};

export default FormRadio;

export const Radio = styled.input.attrs({ type: "radio" })<any>`
  appearance: none;
  z-index: -1;
  position: absolute;
  left: -10px;
  top: -8px;
  display: block;
  margin: 0;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  outline: none;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s;

  &:active {
    opacity: 1;
    transition: opacity 0s;
  }
`;

export const RadioLabelText = styled.span`
  display: inline-block;
  width: 100%;
  cursor: pointer;

  &:before {
    content: "";
    display: inline-block;
    margin: 2px 10px 2px 0;
    border: solid 5px;
    border-color: ${(props: any) => props.theme.colors.lightGrey};
    background-color: ${(props: any) => props.theme.colors.lightGrey};
    border-radius: 50%;
    width: 20px;
    height: 20px;
    vertical-align: top;
    transition: border-color 0.2s;
  }
`;

export const RadioLabel = styled.label<any>`
  position: relative;
  display: inline-flex;
  height: 30px;
  line-height: 1.5;

  > input:checked + span {
    &:before {
      border-color: ${(props: any) => props.theme.colors.primary};
    }
  }

  > input:active + span:before {
    border-color: ${(props: any) => props.theme.colors.primary};
  }

  > input:disabled + span {
    color: ${(props: any) => props.theme.colors.primary};
    cursor: initial;
  }

  &:hover > input {
    opacity: 0.04;
    &:focus {
      opacity: 0.16;
    }
  }
`;

export const RadioLegend = styled.legend`
  margin-bottom: 4px;
  text-transform: uppercase;
  font-size: ${(props: IThemeProps) => props.theme.typography.h100};
  color: ${(props: IThemeProps) =>
    props.theme.colors.inputColor ?? props.theme.colors.primary};
`;
